import { createFileRoute } from '@tanstack/react-router';
import { ContentHeader, ContentSection } from "@/components/content";

export const Route = createFileRoute('/legal/privacy')({
  component: PrivacyPolicyPage,
  head: () => ({
    meta: [
      { title: 'Privacy Policy | Matricks' },
      { name: 'description', content: 'Matricks Privacy Policy. Learn how we collect, use, and protect your personal information.' },
    ],
  }),
});

function PrivacyPolicyPage() {
  return (
    <div className="py-16 sm:py-24">
      <div className="container px-6">
        <ContentHeader
          title="Privacy Policy"
          description="Last updated: December 2024"
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Legal", href: "/legal" },
            { label: "Privacy Policy" },
          ]}
        />

        <div className="prose prose-slate dark:prose-invert max-w-none">
          {/* Introduction */}
          <ContentSection id="introduction" title="Introduction">
            <p className="text-muted-foreground text-lg">
              At Matricks (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;), we are committed to protecting your privacy
              and ensuring the security of your personal information. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when you use our
              platform and services.
            </p>
            <p className="text-muted-foreground">
              By using Matricks, you agree to the collection and use of information in accordance
              with this policy. If you do not agree with our policies and practices, please do not
              use our services.
            </p>
          </ContentSection>

          {/* Information We Collect */}
          <ContentSection id="information-collected" title="Information We Collect" background="muted">
            <div className="space-y-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold text-lg mb-4">Information You Provide</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>
                    <strong>Account Information:</strong> Name, email address, company name, job
                    title, and password when you create an account.
                  </li>
                  <li>
                    <strong>Profile Information:</strong> Additional details you choose to add to
                    your profile, including profile photo and preferences.
                  </li>
                  <li>
                    <strong>Payment Information:</strong> Billing address and payment method
                    details (processed securely by our payment providers).
                  </li>
                  <li>
                    <strong>Communications:</strong> Information you provide when contacting our
                    support team or participating in surveys.
                  </li>
                  <li>
                    <strong>Integration Data:</strong> Credentials and configuration for
                    third-party services you connect through our platform.
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold text-lg mb-4">Information Collected Automatically</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>
                    <strong>Usage Data:</strong> Information about how you use our platform,
                    including features accessed, actions taken, and time spent.
                  </li>
                  <li>
                    <strong>Device Information:</strong> Browser type, operating system, device
                    identifiers, and IP address.
                  </li>
                  <li>
                    <strong>Log Data:</strong> Server logs including request timestamps, referring
                    URLs, and error information.
                  </li>
                  <li>
                    <strong>Cookies and Tracking:</strong> We use cookies and similar technologies
                    for functionality and analytics.
                  </li>
                </ul>
              </div>
            </div>
          </ContentSection>

          {/* How We Use Information */}
          <ContentSection id="how-we-use" title="How We Use Your Information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Service Delivery</h3>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>Providing and maintaining our platform</li>
                  <li>Processing transactions and billing</li>
                  <li>Managing your account and preferences</li>
                  <li>Enabling integrations and workflows</li>
                  <li>Providing customer support</li>
                </ul>
              </div>
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Improvement & Development</h3>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>Analyzing usage patterns and trends</li>
                  <li>Improving platform features and performance</li>
                  <li>Developing new products and services</li>
                  <li>Conducting research and analytics</li>
                  <li>Testing and troubleshooting</li>
                </ul>
              </div>
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Communication</h3>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>Sending service-related notifications</li>
                  <li>Responding to your inquiries</li>
                  <li>Providing updates about our services</li>
                  <li>Sending marketing communications (with consent)</li>
                  <li>Sharing product announcements</li>
                </ul>
              </div>
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Security & Legal</h3>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>Protecting against fraud and abuse</li>
                  <li>Ensuring platform security</li>
                  <li>Complying with legal obligations</li>
                  <li>Enforcing our terms of service</li>
                  <li>Responding to legal requests</li>
                </ul>
              </div>
            </div>
          </ContentSection>

          {/* Data Security */}
          <ContentSection id="data-security" title="Data Security" background="muted">
            <div className="rounded-xl border bg-card p-6">
              <p className="text-muted-foreground mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                <li><strong>Access Controls:</strong> Role-based access controls and multi-factor authentication</li>
                <li><strong>Infrastructure:</strong> Secure cloud infrastructure with regular security audits</li>
                <li><strong>Monitoring:</strong> 24/7 security monitoring and threat detection</li>
                <li><strong>Certifications:</strong> SOC 2 Type II certified security practices</li>
              </ul>
            </div>
          </ContentSection>

          {/* Your Rights */}
          <ContentSection id="your-rights" title="Your Rights">
            <div className="space-y-6">
              <p className="text-muted-foreground">
                Depending on your location, you may have the following rights regarding your
                personal information:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border bg-card p-4">
                  <h4 className="font-semibold">Access</h4>
                  <p className="text-muted-foreground text-sm mt-1">
                    Request a copy of the personal information we hold about you.
                  </p>
                </div>
                <div className="rounded-xl border bg-card p-4">
                  <h4 className="font-semibold">Correction</h4>
                  <p className="text-muted-foreground text-sm mt-1">
                    Request correction of inaccurate or incomplete information.
                  </p>
                </div>
                <div className="rounded-xl border bg-card p-4">
                  <h4 className="font-semibold">Deletion</h4>
                  <p className="text-muted-foreground text-sm mt-1">
                    Request deletion of your personal information in certain circumstances.
                  </p>
                </div>
                <div className="rounded-xl border bg-card p-4">
                  <h4 className="font-semibold">Portability</h4>
                  <p className="text-muted-foreground text-sm mt-1">
                    Request a portable copy of your data in a machine-readable format.
                  </p>
                </div>
              </div>
              <p className="text-muted-foreground">
                To exercise these rights, please contact us at{" "}
                <a href="mailto:privacy@matricks.ai" className="text-primary hover:underline">
                  privacy@matricks.ai
                </a>
                . We will respond to your request within 30 days.
              </p>
            </div>
          </ContentSection>

          {/* Cookies */}
          <ContentSection id="cookies" title="Cookies and Tracking Technologies" background="muted">
            <div className="rounded-xl border bg-card p-6">
              <p className="text-muted-foreground mb-4">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold">Essential Cookies</h4>
                  <p className="text-muted-foreground text-sm">
                    Required for platform functionality, authentication, and security.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold">Analytics Cookies</h4>
                  <p className="text-muted-foreground text-sm">
                    Help us understand how visitors interact with our platform.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold">Preference Cookies</h4>
                  <p className="text-muted-foreground text-sm">
                    Remember your settings and preferences for a better experience.
                  </p>
                </div>
              </div>
              <p className="text-muted-foreground mt-4">
                You can control cookies through your browser settings. Note that disabling certain
                cookies may affect platform functionality.
              </p>
            </div>
          </ContentSection>

          {/* Changes */}
          <ContentSection id="changes" title="Changes to This Policy">
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of any
              material changes by posting the new policy on this page and updating the &quot;Last
              updated&quot; date. We encourage you to review this Privacy Policy periodically for any changes.
            </p>
          </ContentSection>

          {/* Contact */}
          <ContentSection id="contact" title="Contact Us" background="muted">
            <div className="rounded-xl border bg-card p-6">
              <p className="text-muted-foreground mb-4">
                If you have any questions about this Privacy Policy or our data practices, please
                contact us:
              </p>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:privacy@matricks.ai" className="text-primary hover:underline">
                    privacy@matricks.ai
                  </a>
                </p>
                <p>
                  <strong>Address:</strong> Matricks, Inc., Privacy Team
                </p>
              </div>
            </div>
          </ContentSection>
        </div>
      </div>
    </div>
  );
}
